import { useCallback, useEffect, useRef, useState } from "react";
import { createEventSource, type EventSourceClient } from "eventsource-client";
import { syncersService, type JobRun } from "../../../api/syncers.service.js";
import { authTokenManager } from "@legal-500/auth-client";
import styles from "./Syncers.module.css";

type LogViewerProps = {
  run: JobRun | null;
  onClose?: () => void;
};

const STORAGE_KEY_PREFIX = "syncer_logs_";

function LogViewer({ run, onClose }: LogViewerProps) {
  const [logs, setLogs] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);
  const logsContainerRef = useRef<HTMLPreElement>(null);
  const eventSourceRef = useRef<EventSourceClient | null>(null);

  // Load logs - fetch from API for completed/failed jobs
  useEffect(() => {
    if (!run) {
      setLogs("");
      return;
    }

    // Load initial logs from run data or localStorage
    const storageKey = `${STORAGE_KEY_PREFIX}${run.id}`;
    const persistedLogs = localStorage.getItem(storageKey);

    if (run.logs) {
      setLogs(run.logs);
    } else if (persistedLogs) {
      setLogs(persistedLogs);
    } else {
      setLogs("");
    }

    // For completed/failed jobs, fetch full details to get logs
    if (run.status === "completed" || run.status === "failed") {
      syncersService
        .getJobRun(run.id)
        .then(response => {
          if (response.run.logs) {
            setLogs(response.run.logs);
          }
        })
        .catch(error => {
          console.error("Failed to fetch job logs:", error);
        });
    }
  }, [run]);

  // Persist logs to localStorage when they change
  useEffect(() => {
    if (run && logs) {
      const storageKey = `${STORAGE_KEY_PREFIX}${run.id}`;
      localStorage.setItem(storageKey, logs);
    }
  }, [run, logs]);

  // Clean up old logs from localStorage (keep only last 10)
  useEffect(() => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith(STORAGE_KEY_PREFIX));
    if (keys.length > 10) {
      // Remove oldest entries
      keys.slice(0, keys.length - 10).forEach(key => localStorage.removeItem(key));
    }
  }, []);

  // Auto-scroll to bottom when new logs arrive
  const scrollToBottom = useCallback(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [logs, scrollToBottom]);

  // Set up SSE connection for streaming logs when job is running
  useEffect(() => {
    if (!run || (run.status !== "running" && run.status !== "pending")) {
      // Clean up any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsStreaming(false);
      return;
    }

    const sseUrl = syncersService.getLogsStreamUrl(run.id);
    const headers: Record<string, string> = {};
    const token = authTokenManager.getToken();
    if (token) {
      headers["X-L500-Token"] = token;
    }

    // First SSE message contains full logs, subsequent contain only new content
    let isFirstMessage = true;

    const eventSource = createEventSource({
      url: sseUrl,
      headers,
      onMessage: event => {
        try {
          const data = JSON.parse(event.data);
          // Handle logs - backend sends { type: "logs", content: "..." }
          if (data.type === "logs" && data.content) {
            if (isFirstMessage) {
              // First message has full logs - replace
              setLogs(data.content);
              isFirstMessage = false;
            } else {
              // Subsequent messages have only new content - append
              setLogs(prevLogs => prevLogs + data.content);
            }
          }
          // Handle status updates - backend sends { type: "status", status: "..." }
          if (data.type === "status" && (data.status === "completed" || data.status === "failed")) {
            setIsStreaming(false);
            eventSource.close();
          }
        } catch {
          // If not JSON, treat as plain text log
          setLogs(prevLogs => prevLogs + event.data + "\n");
        }
      },
      onDisconnect: () => {
        setIsStreaming(false);
      },
    });

    eventSourceRef.current = eventSource;
    setIsStreaming(true);

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [run]);

  if (!run) {
    return (
      <div className={styles.logViewerEmpty}>
        <p>Select a job run to view logs</p>
      </div>
    );
  }

  return (
    <div className={styles.logViewer}>
      <div className={styles.logViewerHeader}>
        <div className={styles.logViewerTitle}>
          <span>Logs for Run {run.id}</span>
          {isStreaming && <span className={styles.liveBadge}>Live</span>}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close log viewer"
          >
            Close
          </button>
        )}
      </div>
      <pre ref={logsContainerRef} className={styles.logContent}>
        {logs || "No logs available yet..."}
      </pre>
    </div>
  );
}

export default LogViewer;
